import type {
  CommissioningStage,
  CommissioningStepStatus,
  InstallationStages,
  InstallationStageStatus,
} from '../../app/ApiGen';

export type InstallationStageName = keyof InstallationStages;

export type UpdatableInstallationStageName = Exclude<InstallationStageName, 'commissioning'>;

export const installationStageStatuses: InstallationStageStatus[] = [
  'N/A',
  'Not Ready',
  'Ready',
  'In Progress',
  'Completed',
  'Failed',
];

export const commissioningStepStatuses: CommissioningStepStatus[] = installationStageStatuses;

export const HumanReadableInstallationStage: Record<InstallationStageName, string> = {
  preWorkForm: 'Pre-work form',
  commissioning: 'Commissioning',
  postWorkForm: 'Post-work form',
  certificateOfCompliance: 'CoC',
};

export const installationStageOrder: InstallationStageName[] = [
  'preWorkForm',
  'commissioning',
  'postWorkForm',
  'certificateOfCompliance',
];

export function isStageFinished(status: InstallationStageStatus): boolean {
  return status === 'Completed' || status === 'N/A';
}

export function getCurrentInstallationStage(
  installationStages?: InstallationStages,
): InstallationStageName | undefined {
  if (!installationStages) {
    return undefined;
  }
  return installationStageOrder.find((stage) => !isStageFinished(installationStages[stage].status));
}

export type CommissioningStepName = keyof CommissioningStage['steps'];

export const HumanReadableCommissioningStep: Record<CommissioningStepName, string> = {
  wifi: 'Wi-Fi',
  updateFirmware: 'Update firmware',
  supplyMonitoring: 'Supply monitoring',
  chargerSetup: 'Charger setup',
  installationRecord: 'Installation record',
  installationPhotos: 'Installation photos',
  applyConfiguration: 'Apply configuration',
  currentTransformerChecks: 'CT checks',
};

export const commissioningStepOrder: CommissioningStepName[] = [
  'wifi',
  'updateFirmware',
  'supplyMonitoring',
  'chargerSetup',
  'installationRecord',
  'installationPhotos',
  'applyConfiguration',
  'currentTransformerChecks',
];

export function getCurrentCommissioningStep(
  installationStages?: InstallationStages,
): CommissioningStepName | undefined {
  if (!installationStages || getCurrentInstallationStage(installationStages) !== 'commissioning') {
    return undefined;
  }
  const { steps } = installationStages.commissioning;
  return commissioningStepOrder.find((step) => !isStageFinished(steps[step].status));
}

export function getOutstandingCommissioningSteps(installationStages: InstallationStages): CommissioningStepName[] {
  const { steps } = installationStages.commissioning;
  return commissioningStepOrder.filter((step) => !isStageFinished(steps[step].status));
}

export function hasCommissioningSteps(installationStages: InstallationStages): boolean {
  const { steps } = installationStages.commissioning;
  return commissioningStepOrder.some((step) => steps[step].status !== 'N/A');
}

export function isCommissioningFinished(installationStages: InstallationStages): boolean {
  return (
    isStageFinished(installationStages.commissioning.status) ||
    (hasCommissioningSteps(installationStages) && getOutstandingCommissioningSteps(installationStages).length === 0)
  );
}
